import {
  DeepReadonly,
  DocStatusType,
  FollowUpDocStatusType,
  IRegionConfig,
  OptionStatusType,
  RegionConfigType,
  SectionStatusType,
} from 'types';
import { deepFreeze } from '../utils/deepFreeze.js';

export const defaultRegionConfig: DeepReadonly<IRegionConfig> = deepFreeze({
  id: 'string',
  configName: 'defaultConf',
  configDesc: 'static low risk fallback',
  customParentalConsentFormLink: '',
  depositInstructionLink: '',
  studentAutomatedChatReply: '',
  otherGenderEnabled: true,
  universityAccommodationEnabled: true,
  agentDetailsEnabled: true,
  agentCompanyNameForStudentEnabled: false,
  type: RegionConfigType.DEFAULT,
  docSectionStatus: SectionStatusType.ACTIVE,
  finDocSectionStatus: SectionStatusType.ACTIVE,
  interviewSectionStatus: SectionStatusType.ACTIVE,
  historySectionStatus: SectionStatusType.ACTIVE,
  financeSectionStatus: SectionStatusType.ACTIVE,
  followUpDocSectionStatus: SectionStatusType.ACTIVE,
  scheduledInterviewsSectionStatus: SectionStatusType.ACTIVE,
  scheduledInterviews: { calendlyEventType: { id: '' } },
  recordedInterviewSectionStatus: SectionStatusType.ACTIVE,
  beforeArrivalDocAreaStatus: SectionStatusType.ACTIVE,
  afterArrivalDocAreaStatus: SectionStatusType.ACTIVE,
  preDepartureDocSectionStatus: SectionStatusType.ACTIVE,
  afterTravelDocSectionStatus: SectionStatusType.ACTIVE,
  beforeArrivalDocuments: {
    customDocuments: {},
    studentRefusalLetter: FollowUpDocStatusType.REQUIRED,
    immigrationStatusShareCode: FollowUpDocStatusType.NOT_REQUIRED,
    immigrationStatusPdf: FollowUpDocStatusType.NOT_REQUIRED,
    visaApplicationPdfCopy: FollowUpDocStatusType.REQUIRED,
    travelTicket: FollowUpDocStatusType.REQUIRED,
    visaVignette: FollowUpDocStatusType.REQUIRED,
  },
  afterArrivalDocuments: {
    customDocuments: {},
    biometricResidencePermit: FollowUpDocStatusType.REQUIRED,
    stampedVisaVignette: FollowUpDocStatusType.REQUIRED,
  },
  documents: {
    customDocuments: {},
    studentVisas: {
      studentVisa: DocStatusType.REQUIRED,
      cas: DocStatusType.REQUIRED,
      biometric: DocStatusType.REQUIRED,
    },
    cas: DocStatusType.REQUIRED,
    passport: DocStatusType.REQUIRED,
    academicTranscript: DocStatusType.REQUIRED,
    academicCertificate: DocStatusType.REQUIRED,
    visaRefusals: DocStatusType.REQUIRED,
    nonUkVisaRefusals: DocStatusType.REQUIRED,
    otherVisas: {
      visas: DocStatusType.REQUIRED,
      biometric: DocStatusType.REQUIRED,
    },
    parentalConsent: DocStatusType.REQUIRED,
    birthCertificate: DocStatusType.REQUIRED,
    tbTest: DocStatusType.REQUIRED,
    atas: DocStatusType.REQUIRED,
    englishTest: DocStatusType.REQUIRED,
    englishQualification: DocStatusType.REQUIRED,
    customRequest: DocStatusType.REQUIRED,
    postStudyLetter: DocStatusType.REQUIRED,
  },
  financeDocuments: {
    deposit: DocStatusType.REQUIRED,
    birthCertificate: DocStatusType.REQUIRED,
    evidenceOfIncome: {
      curriculumVitae: DocStatusType.REQUIRED,
      employmentReference: DocStatusType.REQUIRED,
      recentEmploymentContract: DocStatusType.REQUIRED,
      recentPayslip: DocStatusType.REQUIRED,
    },
    familySponsorship: {
      bankStatement: DocStatusType.REQUIRED,
      parentLetter: DocStatusType.REQUIRED,
      termDeposit: DocStatusType.REQUIRED,
      verificationBankLetter: DocStatusType.REQUIRED,
      secondaryBankStatement: DocStatusType.REQUIRED,
    },
    governmentSponsorship: DocStatusType.REQUIRED,
    companySponsorship: DocStatusType.REQUIRED,
    scholarshipSponsorship: DocStatusType.REQUIRED,
    educationSponsorship: DocStatusType.REQUIRED,
    customRequest: DocStatusType.REQUIRED,
    other: DocStatusType.REQUIRED,
    customDocuments: {},
  },
  otherOptions: {
    visaSupport: OptionStatusType.ACTIVE,
  },
  Checklist: {
    Consents: {
      Page1: {
        blurb:
          'Please ensure the information you have provided is correct and be sure to fill in any missing information.\n            <br />\n            <br />\n              The University will use this information to obtain your CAS number from the UKVI which, when received by the University, will be sent to the email address you have provided.\n            <br />\n            <br />\n              If a visa application is made with a CAS which has incorrect information, this could lead to the application being refused, immigration permission of the wrong length or with the wrong conditions being granted.\n              <br />\n              <br />\n              I confirm that the information provided in the form is correct.\n        ',
        heading: 'Accuracy of information provided by you',
        hidden: false,
        title: 'Consent 1',
      },
      Page2: {
        hidden: false,
        title: 'Consent 2',
      },
      Page3: {
        hidden: false,
        title: 'Consent 3',
      },
      Page4: {},
      SectionStart: {
        warning:
          'In this section you will acknowledge your responsibilities as a student and confirm relevant consents for your CAS application',
      },
      shortTitle: 'Your Responsibilities',
      title: 'Section 3 - Your Responsibilities',
    },
    defaultOverrides: {
      core: {},
      personalInfo: {},
    },
    Finances: {
      Page1: {
        AccommodationDepositAmount: {
          defaultValue: 0,
          label: 'Accommodation Deposit Amount',
          mandatory: true,
          visible: true,
        },
        AccommodationDepositDate: {
          label: 'Approximately when do you plan on paying your accommodation deposit?',
          mandatory: true,
          visible: true,
        },
        AccommodationDepositPaid: {
          label: 'Have you paid a deposit to the university for university accommodation?',
          mandatory: true,
          visible: true,
        },
        AmountCovered: {
          helpText: 'The amount in pounds that will be used from this account',
        },
        AmountCoveredBankSponsor: {
          helpText: '',
        },
        AmountToProve: {
          helpText: 'To obtain your visa you will need to have access to the funds above',
        },
        CanWithdrawImmediately: {
          label: 'Can the funds from the account be withdrawn immediately?',
        },
        CompanyPresence: {
          helpText: 'e.g. Does it operate outside of its home country',
        },
        CoverType: {
          label: 'How much is your sponsor (or scholarship) providing for your tuition fees?',
          mandatory: true,
          visible: true,
        },
        DepositAmount: {
          defaultValue: 0,
          helpText:
            'Enter the total deposit you have transferred to the university in GBP (british pounds). Please note if you have deposited less than the minimum deposit of £3000 you will need to make another transfer to make up the difference before your CAS can be issued.',
          label: 'Deposit Amount',
          mandatory: true,
          visible: true,
        },
        DepositDate: {
          helpText: 'Tell us the approximate date you plan to make your deposit payment',
          label: 'When do you expect to pay your deposit?',
          mandatory: true,
          visible: true,
        },
        DepositPaid: {
          helpText:
            "<span>Answer 'yes' if you have paid a contribution towards your course fees.<br /><br />A minimum deposit of £3000 must be made before the university can process your CAS.</span>",
          label: 'Have you paid a course fee deposit?',
          mandatory: true,
          visible: true,
        },
        DepositReason: {
          label: "Please provide more information on why a deposit isn't required",
          mandatory: true,
          visible: true,
        },
        EducationLoanName: {
          helpText: 'Select the person whose name is on the loan. If your name is on the loan select “my name”.',
        },
        FundingSources: {},
        FundsMatured: {
          helpText: 'Has the full amount of required funds been in this account for 28 days?',
        },
        FundsMatureDate: {
          helpText: 'What date will the funds have been in this account for 28 days?',
        },
        HasSeparateLivingCost: {
          label: 'Are your living costs listed separately from your tuition in your sponsorship documentation?',
          mandatory: true,
          visible: true,
        },
        MonthlyLivingCost: {
          label: 'What is the monthly amount for living costs?',
          mandatory: true,
          visible: true,
        },
        MonthlyOrUpfrontLivingCost: {
          label:
            'Will your living costs be provided to you as a monthly stipend / payment or provided to you as an upfront payment?',
          mandatory: true,
          visible: true,
        },
        OtherFundingDesc: {
          helpText: 'Explain in your own words, maximum 2 sentences',
        },
        RelationshipToPerson: {
          helpText: '',
        },
        RemainingAmountToProve: {
          helpText: 'You cannot proceed until you have proven how you will fund your studies and maintenance costs',
        },
        ScholarshipName: {
          helpText: 'Enter the name / type of scholarship you received ',
        },
        ScholarshipNature: {
          helpText: 'How much of your course fee and/or living expenses does your scholarship cover?',
        },
        title: 'Source of Funds and Calculator',
        TotalAmountInAccount: {
          helpText:
            'If your account is not in GBP, use the latest exchange rate value to convert your currency to GBP. UKVI use <a class="studentAppLinks" href="https://www.oanda.com/currency-converter/en/?from=EUR&to=GBP&amount=1" target="_blank">Oanda</a> exchange rate.',
          subLabel: 'Enter the amount currently in your account to the nearest pound sterling (GBP).',
        },
        TotalLivingCost: {
          label: 'What is the total amount for living costs?',
          mandatory: true,
          visible: true,
        },
      },
      shortTitle: 'Finance',
      title: 'Section 4 - Your Financial Plan',
    },
    History: {
      Page1: {
        HasOverseasTravel: {
          helpText: 'Includes any country outside the border of the country you currently live in',
        },
        HasPreviousUkStudy: {
          visible: true,
        },
        HasPreviousUkStudyVisa: {
          visible: true,
        },
        HasUKTravel: {},
        LastCourse: {
          helpText:
            'Please choose the option that accurately represents the course you have either completed most recently or are expected to complete by the time you apply for your visa.',
        },
        LastCourseOther: {},
        LastCourseYear: {
          helpText:
            'Please enter the year you graduated from, or will graduate from, the course you just declared in the previous question.',
        },
        OverseasTravelTimes: {},
        PreviousUkStudyCourseComplete: {},
        PreviousUkStudyIncopleteRecorded: {},
        PreviousUkStudyLevel: {},
        PreviousUkStudyReasonNotCompleteCourse: {},
        PreviousUkStudyVisaShortened: {},
        PreviousUkStudyVisaShortenedDate: {},
        PreviousUkStudyVisaType: {},
        PreviousUkStudyYearOfReceived: {},
        LastCourseLevel: {
          helpText: 'Relates only to the last academic course you have studied in the UK on a TIER 4 visa',
        },
        LowerOrSameLevelReason: {},
        title: 'Prior Study and Travel',
        UKTravelTimes: {},
      },
      Page2: {
        PrevNonUKVisaRefusal: {
          helpText:
            '\n            <span>\n              This question relates to any visa refusals from countries other than the\n              UK.\n              <br />\n              <br />A visa refusal is a letter or email issued by a government after a\n              visa application. A refusal denies your request to enter the country.\n            </span>\n          ',
        },
        PrevNonUKVisaRefusalCountry: {},
        PrevNonUKVisaRefusalStudent: {},
        PrevNonUKVisaRefusalYear: {},
        PrevRejectionReason: {},
        PrevRejections: {
          helpText: 'For example at a UK airport or seaport',
        },
        PrevUKOtherVisa: {
          helpText:
            'This question relates to any non-student UK visas, which are typically represented as stickers or vignettes in your passport, granting you permission to enter the country. Standard entry stamps should not be included in your response.',
        },
        PrevUKOtherVisaType: {
          helpText: "Select 'Other' if you can't find your visa type",
        },
        PrevUKOtherVisaYear: {},
        PrevVisaRefusal: {
          helpText:
            '\n            <span>\n              This question relates to UK visa refusals only.\n              <br />\n              <br />A visa refusal is a letter or email issued by a government after a\n              visa application. A refusal denies your request to enter the country.\n            </span>\n          ',
        },
        PrevVisaRefusalType: {
          helpText: "Select other if you can't find your visa type",
        },
        PrevVisaRefusalYear: {},
        title: 'Prior Travel and Refusals',
        UKLeaveToRemain: {
          helpText: 'Have you ever applied to live permanently in the UK',
        },
      },
      Page3: {
        ChildrenTravelling: {},
        ChildrenTravellingNumber: {},
        FamilyMembersTravelling: {
          helpText:
            'This question refers to whether you will be coming to the UK alone or plan to bring any dependants or family members with you. <br><br> For the latest rules and regulations regarding students bringing dependants, please refer to the latest government guidance on the <a class="studentAppLinks" href="https://www.gov.uk/student-visa/family-members" target="_blank">gov.uk website</a>.',
        },
        HomeCountryApplication: {
          helpText:
            'Please confirm whether you intend to submit your student visa application in the country you have declared as your nationality above. This question refers to the location of your visa application',
        },
        HomeCountryApplicationReason: {},
        SpouseTravelling: {},
        PartnerVisaStatus: {},
        title: 'Travellers and Dependants',
      },
      shortTitle: 'Your History',
      title: 'Section 2 - Your History',
    },
    InfoProcessing: {
      blurb:
        'I understand that the CAS preparation process will require the collection of personal and sensitive information that belongs to me. This data will be processed by the university and approved representatives for the purpose of my CAS application and enrolment.',
      title: 'Processing of Information',
    },
    Landing: {
      blurb:
        'In this process you will be asked a series of questions and given a recommended set of documents to be uploaded in advance of your CAS application review by the university.\n        <br />\n        <br />\n        If your CAS has already been issued, you will only be required to provide visa and arrival updates and upload follow-up documents on CAS Shield.\n        <br />\n        <br />\n        The list of documents and guidelines provided in this process do not constitute legal advice, the purpose is to speed up your preparation.\n        <br />\n        <br />\n        Any official visa and immigration advice will be provided as needed by our university staff and/or an assigned solicitor. If you receive advice from these parties, please ensure you follow it carefully.\n      ',
      title: 'Welcome to the CAS preparation process',
    },
    PersonalInfo: {
      Page1: {
        FamilyName: {
          helpText: 'If you only have one name, enter it here and type <code>-</code> as your given name.',
          label: 'Family Name',
          subLabel: 'Family name must match your passport exactly.',

          mandatory: true,
          visible: true,
        },
        GivenName: {
          helpText: '',
          label: 'Given Name',
          subLabel: 'Given names must match your passport exactly, including any middle names.',
          mandatory: true,
          visible: true,
        },
        HasOtherNames: {
          label: 'In addition to the names already provided, are you now or have you ever been known by another name?',
          mandatory: true,
          visible: true,
        },
        hidden: false,
        MiddleName: {
          helpText: '',
          label: 'Other/Middle Name',
          mandatory: false,
          visible: false,
        },
        OtherNameFamilyName: {
          subLabel: 'Family name must match your passport exactly.',
        },
        OtherNameGivenName: {},
        OtherNameReason: {
          helpText:
            '<span>Please provide context for this name.<br /></br />If you changed your name please include the date it was changed.',
        },
        title: 'Names and Aliases',
      },
      Page10: {
        hidden: false,
        title: 'WhatsApp',
        WhatsApp: {},
        WhatsAppPhoneNumber: {},
      },
      Page2: {
        AppliedForEUSS: {},
        GrantedEUSSStatus: {},
        BirthCountry: {
          helpText: 'The country you were born in',
          label: 'Country of Birth',
          mandatory: true,
          visible: true,
        },
        DOB: {
          helpText: 'As per your passport.',
          label: 'Date of Birth',
          mandatory: true,
          visible: true,
        },
        Gender: {
          helpText: 'As per your passport',
          label: 'Gender',
          mandatory: true,
          visible: true,
        },
        HasOtherNationality: {
          helpText: 'If you have dual nationality or citizenship, select “yes”',
          label: 'Do you currently hold, or have you ever held, any other nationality or citizenship?',
          mandatory: true,
          visible: true,
        },
        hidden: false,
        IsCountryOfNationality: {
          helpText:
            'Please confirm whether you intend to submit your student visa application in the country you have declared as your nationality above. This question refers to the location of your visa application',
          label: 'Are you applying from the country of your nationality?',
          mandatory: true,
          visible: true,
        },
        CountryOfVisaApplication: {
          label: 'In which country will you make your visa application?',
          mandatory: true,
          visible: true,
        },
        LivedInUKLast12Months: {
          helpText:
            'Select "Yes" if on the date of your visa application, you have lived in the UK for 12 months or more without any gaps. Otherwise, select "No"',
          label: 'Have you lived in the UK continuously for over 12 months?',
          subLabel: '',
          mandatory: true,
          visible: true,
        },
        Nationality: {
          helpText:
            'Enter your nationality/citizenship as per the passport or travel document you will use for your visa application. <br> You will be asked about additional or previous nationalities at the end of this section.',
          label: 'Nationality',
          mandatory: true,
          visible: true,
        },
        OtherNationality: {
          label: 'Other Nationality',
          mandatory: true,
          visible: true,
        },
        OtherNationalityCurrent: {
          helpText:
            'Select “current” if you hold this nationality now, or “former” if you held this nationality in the past',
          label: 'Is this a current or former nationality?',
          mandatory: true,
          visible: true,
        },
        OtherNationalityNumber: {
          helpText: 'Including current and former nationalities/citizenships',
          label: 'How many other nationalities?',
          mandatory: true,
          visible: true,
        },
        ReasonWhyNotCountryOfNationality: {
          label: 'Please tell us why you are applying from a country other than the country of your nationality?',
        },
        title: 'Gender, Birth and Nationality',
      },
      Page3: {
        City: {
          label: 'City',
          mandatory: true,
          visible: true,
        },
        Country: {
          label: 'Country',
          mandatory: true,
          visible: true,
        },
        hidden: false,
        HomeAddress: {
          helpText: 'Your current home / residential address e.g. Where you live',
          label: 'Permanent Residential Address',
          mandatory: true,
          subLabel: 'Address (e.g. unit 33, 17 Example Street)',
          visible: true,
        },
        PhoneNumber: {
          helpText: '',
          label: 'Phone Number',
          mandatory: true,
          visible: true,
        },
        Postcode: {
          label: 'Postcode (if applicable)',
          mandatory: false,
          visible: true,
        },
        title: 'Contact and Address',
      },
      Page4: {
        CriminalConvictionsReason: {
          label: 'Please provide more information on the date and nature of the offence(s)',
          mandatory: true,
          visible: true,
        },
        HasCriminalConvictions: {
          helpText:
            "Please respond 'yes' if you currently have any of the following: a criminal conviction, a penalty for a driving offense (e.g. speeding or driving without insurance), an ongoing arrest or charge awaiting trial, a caution, warning, reprimand, or another out-of-court penalty, a civil court judgment (e.g. for non-payment of debt, bankruptcy, or antisocial behavior), or a civil penalty.",
          label: 'Have you ever had any criminal convictions, charges, or penalties in the UK or any other country?',
          mandatory: true,
          visible: true,
        },
        hidden: false,
        title: 'Criminal Convictions',
      },
      Page5: {
        AutoCourseName: {
          helpText: 'Please select the same course name as listed on your offer letter',
          label: 'Course Name',
          mandatory: true,
          visible: true,
        },
        CAH3: {
          helpText: 'If present on your offer letter (e.g. CAH10-01-02)',
          label: 'CAH3 Code',
          mandatory: false,
          visible: true,
        },
        Campus: {
          helpText: 'As per your offer letter',
          label: 'Select your campus',
          mandatory: false,
          visible: false,
        },
        CASReceived: {
          label: 'Have you received your CAS?',
          mandatory: false,
          visible: false,
        },
        CourseFee: {
          defaultValue: null,
          helpText: '',
          label: 'First Year Course Fee',
          mandatory: true,
          subLabel: '',
          visible: true,
        },
        CourseScholarshipAmount1: {
          subLabel: 'As stated in your offer letter ',
          visible: true,
          mandatory: false,
          defaultValue: null,
          helpText:
            ' If you have been awarded any additional scholarships or discounts that are not listed on this page, you will be able to add them later',
        },
        CourseLevel: {
          label: 'What is the level of your course?',
          mandatory: true,
          visible: true,
        },
        CourseLevelOther: {},
        DepositAmount: {
          helpText:
            'Please note if you have deposited less than the minimum deposit of 50% first year tuition fee you will need to make another transfer to make up the difference before your CAS can be issued',
        },
        DepositPaid: {
          helpText:
            "<span>            Answer 'yes' if you have paid a contribution towards your course fees.            <br />            <br />A minimum deposit of 50% first year tuition fee must be made            before the university can process your CAS.          </span>",
        },
        EndDate: {
          defaultValue: null,
          helpText: '',
          label: 'Course End Date',
          mandatory: true,
          visible: false,
        },
        hidden: false,
        IsWorkPlacement: {
          label: 'Work Placement',
          helpText: 'Select "yes", if your course includes a work placement',
          mandatory: true,
          visible: true,
        },
        ManualCourseName: {
          helpText: 'As per your offer letter',
          label: 'Course Name',
          mandatory: true,
          subLabel: '',
          visible: true,
        },
        StartDate: {
          defaultValue: null,
          helpText: '',
          label: 'Course Start Date',
          mandatory: true,
          visible: true,
        },
        StudentNumber: {
          label: 'Student Number',
          mandatory: true,
          subLabel: 'All details are as per your offer letter',
          visible: true,
        },
        title: 'Student Number and Course',
      },
      Page5_1: {
        title: 'Evidence Used to Obtain Offer',
        hidden: true,
        OfferEvidence: {
          label: '',
          subLabel: 'Please make your corrections to the text or add a note at the bottom',
          visible: true,
          mandatory: true,
          helpText: '',
        },
      },
      Page6: {
        EnglishTestNotReqdReason: {
          label: 'How did you meet the English language requirement?',
          visible: true,
          mandatory: true,
        },
        EnglishTestNotReqdReasonOther: {
          label: 'Please explain',
          visible: true,
          mandatory: true,
        },
        Qualification: {
          label: 'Which qualification?',
          visible: true,
          mandatory: true,
        },
        QualificationOther: {
          label: 'What is the name of the qualification?',
          mandatory: true,
          visible: true,
        },
        qualificationOther: {
          label: 'What is the name of the qualification?',
          mandatory: true,
          visible: true,
        },
        EnglishTestType: {
          label: 'What type of English test?',
          mandatory: true,
          visible: true,
        },
        EnglishTestTypeOther: {
          label: 'What is the name of the English test you completed?',
          mandatory: true,
          visible: true,
        },
        HasEnglishTest: {
          label: 'Were you required to submit an English test as part of your application?',
          mandatory: true,
          visible: true,
        },
        title: 'English Test',
      },
      Page7: {
        PassportExpectedDate: {
          helpText: 'Enter the approximate date you expect to receive your new passport.',
          label: 'When do you expect to receive your passport?',
          mandatory: true,
          visible: true,
        },
        PassportExpiryDate: {
          helpText:
            'Note, if your passport expires within 6 months of your course start date, a new passport copy will be requested later. If you are unable to provide the new passport information, please contact the admissions team.',
          label: 'Passport Expiry Date',
          mandatory: true,
          visible: true,
        },
        PassportNumber: {
          label: 'Passport Number',
          mandatory: true,
          visible: true,
        },
        PassportStatus: {
          helpText: '',
          label: 'Do you currently have a passport?',
          mandatory: true,
          visible: true,
        },
        hidden: false,
        title: 'Passport Details',
      },
      Page8: {
        AgentNameConfig: {
          label: 'Who is helping you?',
          mandatory: true,
          visible: true,
        },
        AutoAgentName: {
          label: 'What is the name of your agency/agent?',
          mandatory: true,
          visible: true,
        },
        hidden: false,
        ManualAgentName: {
          label: 'Manually Enter Agent Name',
          mandatory: true,
          visible: true,
        },
        title: 'Enrolment Support',
        WithAgent: {
          helpText: 'An "Agent" is a local office or business that helps students choose and apply to universities',
          label: 'Did anyone help you submit your application to study with us?',
          mandatory: true,
          visible: true,
        },
      },
      Page9: {
        hidden: false,
        title: 'Visa Support',
        VisaSupport: {
          helpText: '',
        },
      },
      SectionStart: {
        warning:
          '<strong>Note:</strong> The requested information may be used in official documents. Please ensure you enter accurate information.',
      },
      shortTitle: 'Personal Information',
      title: 'Section 1 - Personal Information',
    },
    Summary: {
      Complete: {
        title: 'CAS Checklist Complete!',
        warning: 'Continue to your task list for document upload and a Pre-CAS Questionnaire',
      },
      Incomplete: {
        title: "You've reached the end of the CAS Checklist",
      },
    },
  },
  Interview: {
    Page1: {
      CourseReasons: {
        helpText: 'You can select more than one option',
      },
      CourseReasonsOther: {
        helpText: 'Maximum 2 sentences',
      },
      UniChoiceReason1: {
        helpText: 'Maximum 2 sentences per reason',
      },
      UniChoiceReason2: {
        helpText: 'Maximum 2 sentences per reason',
      },
      UniChoiceReason3: {
        helpText: 'Maximum 2 sentences per reason',
      },
      UniReasons: {
        helpText: 'You can select more than one option',
      },
      UniReasonsOther: {
        helpText: 'Maximum 2 sentences',
      },
      WhyUK: {
        helpText: 'Select the option that best describes your reason for studying in the UK',
      },
      WhyUKOther: {
        helpText: 'Maximum 2 sentences',
      },
    },
    Page2: {
      Campus: {
        helpText: 'If you are not sure please refer to your offer letter or the university website',
      },
      ConisderOtherCourseName: {
        helpText: 'Please enter the title of the course you have considered',
      },
      ConsiderOtherCourseReason: {
        helpText: 'Explain in your own words',
      },
      ConsiderOtherUni: {
        helpText:
          'Entering other universities here will not affect your application, we encourage students to be well researched',
      },
      ConsiderOtherUniReason: {
        helpText: '',
      },
      CourseAlignCareerReason: {
        helpText: 'Explain in your own words, maximum 2 sentences',
      },
      UniResearchMethod: {
        helpText: 'You can select more than one option',
      },
    },
    Page3: {
      CourseAndLivingWithoutWorking: {
        helpText: 'It is important there is nothing hindering your ability to pay your fees or living expenses',
      },
      CourseAndLivingWithoutWorkingReason: {
        helpText: 'Explain in your own words, maximum 3 sentences',
      },
      FurtherStudyCareerPlan: {
        helpText: 'Explain in your own words, maximum 3 sentences',
      },
      WorkWhileStudy: {
        helpText: 'This refers to working part time',
      },
      WorkWhileStudyReason: {
        helpText: 'Explain in your own words, maximum 3 sentences',
      },
    },
    Page4: {},
    SectionComplete: {
      title: 'Pre-CAS Questionnaire Complete!',
      warning: 'Go to task list to complete the remaining items',
    },
    SectionStart: {
      title: 'Pre-CAS Questionnaire',
    },
  },
  sections: {
    ScheduledInterviews: {
      label: '',
    },
    CASChecklist: {
      label: 'Checklist',
      summary:
        'On this page you can edit the answers you have provided to the checklist, or resume the process if you are yet to complete it.',
      summaryLabel: 'Checklist Summary',
    },
    FinanceDocuments: {
      description: 'Please upload or review document requirements as requested',
      label: 'Financial Documents',
      summary:
        'As part of this CAS application or your upcoming visa application you may need to provide documents that prove you have access to the required #TotalFundingToShow in expenses as outlined in the financial plan section. <p>Click ‘How to Prove’ or ‘Please Review‘ as requested to see the specific requirements for each document.</p>',
      summaryLabel: 'Proving your finances',
    },
    GeneralDocument: {
      description: 'Please upload or review document requirements as requested',
      label: 'General Documents',
      summary:
        'Below is a list of academic, travel and other documents associated with your CAS and visa application. This list is based on the answers you provided to the CAS Checklist questions.',
      summaryLabel: 'General Documents',
    },
    PreCASInterview: {
      description: 'Answer a few questions about your upcoming overseas study',
      label: 'Pre-CAS Questionnaire',
      summary: 'Below is a series of questions built to evaluate credibility for a CAS to be issued',
      summaryLabel: 'Pre-CAS Questionnaire',
    },
    ReceiveCAS: {
      description: 'Your CAS will not be issued until the above tasks are completed',
      label: 'Receive CAS Document',
    },
    Registration: {
      description: 'On-campus registration',
      label: '15th of January',
    },
    VisaApplication: {
      description: 'Begin the visa and immigration process',
      label: 'Start Visa Application',
    },
    FollowUpDocuments: {
      label: 'Follow-up Documents',
      description: 'Please upload or review document requirements as requested',
      summaryLabel: 'Follow-up Documents',
      summary: 'Please read and complete the tasks below.',
    },
  },
});
